import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Lock, Paperclip } from "lucide-react";
import { formatApiError } from "../../lib/api";
import {
  listCases, getCase, replyToCase, addInternalNote, assignCase, setPriority,
  setStatus, escalateCase, mergeCase, caseAuditTrail, myAgentProfile,
  CASE_STATUSES, CASE_PRIORITIES,
} from "../../lib/supportWorkspace";
import AiAssistancePanel from "../../components/support-workspace/AiAssistancePanel";

const STATUS_TONE = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  open: "bg-amber-50 text-amber-700 border-amber-200",
  waiting_customer: "bg-slate-50 text-slate-600 border-slate-200",
  waiting_confirmph: "bg-rose-50 text-rose-700 border-rose-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-slate-100 text-slate-500 border-slate-200",
};

function StatusBadge({ status }) {
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium uppercase tracking-wide ${STATUS_TONE[status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

export default function SupportWorkspaceCases() {
  const [caseId, setCaseId] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => { myAgentProfile().then(setProfile).catch(() => {}); }, []);

  if (caseId) {
    return (
      <CaseDetail caseId={caseId} profile={profile} onBack={() => setCaseId(null)} />
    );
  }
  return <CaseQueue profile={profile} onOpen={setCaseId} />;
}

function CaseQueue({ profile, onOpen }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatusFilter] = useState("");
  const [queue, setQueueFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (queue) params.queue = queue;
      const rows = await listCases(params);
      setItems(rows);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status, queue]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="support-workspace-case-queue">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-xl font-semibold text-slate-900">Support cases</h1>
        {profile && (
          <span className="text-xs text-slate-500">
            Signed in as {profile.title || "Agent"} · scope: {profile.scope}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={status} onChange={(e) => setStatusFilter(e.target.value)} data-testid="support-queue-status-filter"
                className="text-sm border border-slate-300 rounded-lg px-2 py-1.5">
          <option value="">All statuses</option>
          {CASE_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select value={queue} onChange={(e) => setQueueFilter(e.target.value)} data-testid="support-queue-queue-filter"
                className="text-sm border border-slate-300 rounded-lg px-2 py-1.5">
          <option value="">All queues</option>
          <option value="general">General</option>
          <option value="technical">Technical</option>
          <option value="account_recovery">Account recovery</option>
          <option value="security">Security</option>
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          No cases match these filters.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <button key={c.id} onClick={() => onOpen(c.id)} data-testid={`support-case-row-${c.id}`}
                    className="w-full text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-emerald-500 transition flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-slate-400">{c.case_reference}</span>
                  {c.restricted && <Lock className="w-3.5 h-3.5 text-rose-500" aria-label="Restricted" />}
                </div>
                <div className="font-medium text-slate-900 truncate">{c.subject}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {c.requester_name} · {c.queue} · priority {c.priority}
                </div>
              </div>
              <StatusBadge status={c.status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CaseDetail({ caseId, profile, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [noteText, setNoteText] = useState("");
  const [audit, setAudit] = useState(null);
  const caps = profile?.capabilities || [];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getCase(caseId);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, successMsg) => {
    try {
      await fn();
      toast.success(successMsg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading || !data) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }
  const { case: kase, messages, internal_notes: notes, context } = data;

  return (
    <div data-testid="support-workspace-case-detail">
      <button onClick={onBack} data-testid="support-case-back" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to queue
      </button>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="min-w-0">
          <div className="rounded-xl border border-slate-200 bg-white p-5 mb-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-slate-400">{kase.case_reference}</span>
              {kase.restricted && <Lock className="w-3.5 h-3.5 text-rose-500" />}
              <StatusBadge status={kase.status} />
            </div>
            <h1 className="font-display text-lg font-semibold text-slate-900">{kase.subject}</h1>
            <p className="text-xs text-slate-500 mt-1">
              {kase.requester_name} ({kase.requester_email}) · {kase.queue} · priority {kase.priority}
              {kase.reopen_count > 0 && ` · reopened ${kase.reopen_count}x`}
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 mb-4">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Conversation</h2>
            <ul className="space-y-3">
              {messages.map((m) => (
                <li key={m.id} data-testid={`support-message-${m.id}`}
                    className={`rounded-lg p-3 text-sm ${m.sender_type === "agent" ? "bg-emerald-50" : "bg-slate-50"}`}>
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span className="font-medium text-slate-700">{m.sender_name} ({m.sender_type})</span>
                    <span>{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                  <p className="whitespace-pre-wrap text-slate-800">{m.text}</p>
                  {m.attachment_ids?.length > 0 && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                      <Paperclip className="w-3 h-3" /> {m.attachment_ids.length} attachment(s)
                    </div>
                  )}
                </li>
              ))}
            </ul>
            {caps.includes("reply") && (
              <form onSubmit={(e) => { e.preventDefault(); if (!replyText.trim()) return;
                    run(() => replyToCase(caseId, replyText), "Reply sent").then(() => setReplyText("")); }}
                    className="mt-4 flex gap-2">
                <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)}
                          data-testid="support-reply-textarea" rows={2} placeholder="Reply to requester…"
                          className="flex-1 text-sm border border-slate-300 rounded-lg px-3 py-2" />
                <button type="submit" data-testid="support-reply-submit" className="btn-brand px-4 self-start">Send</button>
              </form>
            )}
          </div>

          {caps.includes("ai_suggestions") && (
            <AiAssistancePanel caseId={caseId} caps={caps} onUseReply={(text) => setReplyText(text)} />
          )}

          {caps.includes("internal_notes") && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-5">
              <h2 className="text-sm font-semibold text-amber-800 mb-3">Internal notes (staff only)</h2>
              <ul className="space-y-2 mb-3">
                {notes.map((n) => (
                  <li key={n.id} data-testid={`support-note-${n.id}`} className="rounded-lg bg-white p-3 text-sm border border-amber-200">
                    <p className="whitespace-pre-wrap text-slate-800">{n.text}</p>
                    <span className="text-[11px] text-slate-400">{new Date(n.created_at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
              <form onSubmit={(e) => { e.preventDefault(); if (!noteText.trim()) return;
                    run(() => addInternalNote(caseId, noteText), "Note added").then(() => setNoteText("")); }}
                    className="flex gap-2">
                <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)}
                          data-testid="support-note-textarea" rows={2} placeholder="Add an internal note…"
                          className="flex-1 text-sm border border-amber-300 rounded-lg px-3 py-2 bg-white" />
                <button type="submit" data-testid="support-note-submit"
                        className="px-4 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 self-start">
                  Add note
                </button>
              </form>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Actions</h2>
            <div className="space-y-2 text-sm">
              {caps.includes("priority") && (
                <label className="block">
                  Priority
                  <select value={kase.priority} data-testid="support-priority-select"
                          onChange={(e) => run(() => setPriority(caseId, e.target.value), "Priority updated")}
                          className="w-full mt-1 border border-slate-300 rounded-lg px-2 py-1.5">
                    {CASE_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
              )}
              {caps.includes("close") && (
                <label className="block">
                  Status
                  <select value={kase.status} data-testid="support-status-select"
                          onChange={(e) => run(() => setStatus(caseId, e.target.value), "Status updated")}
                          className="w-full mt-1 border border-slate-300 rounded-lg px-2 py-1.5">
                    {CASE_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </label>
              )}
              {caps.includes("technical_escalation") && (
                <button data-testid="support-escalate-technical"
                        onClick={() => run(() => escalateCase(caseId, "technical"), "Escalated (technical)")}
                        className="w-full text-left px-3 py-1.5 rounded-lg border border-slate-300 hover:border-emerald-600">
                  Escalate: technical
                </button>
              )}
              {caps.includes("security_escalation") && (
                <button data-testid="support-escalate-security"
                        onClick={() => run(() => escalateCase(caseId, "security"), "Escalated (security)")}
                        className="w-full text-left px-3 py-1.5 rounded-lg border border-rose-300 text-rose-700 hover:border-rose-500">
                  Escalate: security
                </button>
              )}
              {caps.includes("merge") && (
                <button data-testid="support-merge-button" onClick={() => {
                        const dup = window.prompt("Duplicate case ID to merge into this one:");
                        if (dup) run(() => mergeCase(caseId, dup.trim()), "Cases merged");
                      }}
                      className="w-full text-left px-3 py-1.5 rounded-lg border border-slate-300 hover:border-emerald-600">
                  Merge a duplicate case in…
                </button>
              )}
              {caps.includes("assign") && (
                <button data-testid="support-assign-me" onClick={() => run(() => assignCase(caseId, profile.id), "Assigned to you")}
                        className="w-full text-left px-3 py-1.5 rounded-lg border border-slate-300 hover:border-emerald-600">
                  Assign to me
                </button>
              )}
              {caps.includes("audit_read") && (
                <button data-testid="support-view-audit" onClick={() => caseAuditTrail(caseId).then(setAudit).catch((e) => toast.error(formatApiError(e)))}
                        className="w-full text-left px-3 py-1.5 rounded-lg border border-slate-300 hover:border-emerald-600">
                  View audit trail
                </button>
              )}
            </div>
          </div>

          {context?.company && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Business context</h2>
              <p className="font-medium text-slate-800">{context.company.name}</p>
              <p className="text-xs text-slate-500">{context.company.email} · plan {context.company.plan_type}</p>
            </div>
          )}
          {context?.booking && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Linked booking</h2>
              <p className="font-medium text-slate-800">{context.booking.booking_reference}</p>
              <p className="text-xs text-slate-500">{context.booking.status}</p>
            </div>
          )}
          {audit && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm" data-testid="support-audit-panel">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Audit trail</h2>
              <ul className="space-y-1 text-xs text-slate-600">
                {audit.map((row) => (
                  <li key={row.id}>{new Date(row.created_at).toLocaleString()}: {row.description}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
